import { urlParams, timestampSeconds, dateStringsToObjects, dateStringToObject, setLocalStorageSessionItems } from "@/utils";
import slotsCompiler from "@/slots/available-slots-compiler";
import { newDate, dateAdd, dateFormat, isDevMode } from "@/utils";
import clone from "just-clone";

export default {
    initialise(state) {
        let year = (new Date()).getFullYear();
        state.dobYears = [];
        for (let i = 12; i <= 100; i++) state.dobYears.push({ value: year - i, text: `${year - i}` });

        state.urlPath = (new URL(document.location.href)).pathname.replace(/\/+$/, "");

        state.queryStringParams = urlParams();
    },
    setWindowInnerWidth(state) {
        state.windowInnerWidth = window.innerWidth;
    },
    checkForSessionExpiredError(error) {
        const status = error.response?.status ?? 500;
        if(status == 419) {
            this.addToastMessage("Sorry - your session has expired.");
        }
    },
    checkResponseForSessionExpired(state, response) {
        if(response.data.session_expired) {
            state.toastMessages.push("Sorry - your session expired.");
        }
    },
    setIsInitialised(state, { key, value }) { state.isInitialised[key] = value; },
    setSessionIsInitialised(state, val) { state.isInitialised.session = val ? true : false; },
    setSessionIsLoading(state, isLoading) { state.session.isLoading = isLoading; },
    setSession(state, data) {
        data.created_at = dateStringToObject(data.created_at, true);
        data.updated_at = dateStringToObject(data.updated_at, true);
        state.session.data = data;
        //  Write session into local storage
        setLocalStorageSessionItems(data);
    },
    setLandingUrl(state) {
        if(state.landingUrl === false) {
            state.landingUrl = window.location.href;
        }
    },
    setClinicsIsLoading(state, isLoading) { state.clinics.isLoading = isLoading; },
    setServerTimeDiffSeconds(state, serverTime) {
        state.serverTimeDiffSeconds = serverTime - timestampSeconds();
    },
    setClinics(state, data) { 
        state.clinics.data = data.filter(clinic => {
            return isDevMode || clinic.allow_online_bookings;
        });
    },
    setDropdownClinics(state) {
        state.clinics.dropdownLocations = state.clinics.data.map(c => { return { value: c, text: c.name } });
        state.clinics.dropdownLocations.unshift({ value: null, text: "Choose one" });
    },
    setDCsIsLoading(state, isLoading) { state.dcs.isLoading = isLoading; },
    setDCs(state, data) { state.dcs.data = data; },
    setDropdownDCs(state) {
        state.dcs.dropdownDCs = state.clinics.data.filter(clinic => clinic.allow_online_bookings).map(clinic => {
            const dcs = state.dcs.data.filter(dc => dc.location_id == clinic.id);
            return {
                label: clinic.name,
                options: dcs.map(dc => {
                    return { value: dc, text: `${dc.first_name} ${dc.last_name}` };
                })
            };
        });

        // state.dcs.dropdownDCs = state.dcs.data.map(dc => { return { value: dc, text: `${dc.first_name} ${dc.last_name} (${dc.location_name} clinic)` } });
        state.dcs.dropdownDCs.unshift({ value: null, text: "Choose one" });
    },
    setTypeaheadDCs(state) {
        state.dcs.typeaheadDCs = state.dcs.data
            .filter(dc => dc.are_referrals_allowed)
            .map(dc => {
                return {
                    id: dc.id,
                    location_id: dc.location_id,
                    first_name: dc.first_name || "",
                    last_name: dc.last_name || "",
                    name: (`${dc.first_name || ""} ${dc.last_name || ""}`).trim(),
                    search_text: (`${dc.last_name || ""}|${dc.first_name || ""}|${dc.search_text}`).toLowerCase().replace(/[^a-z|]/, "")
                }
            }).sort((a, b) => (a.search_text > b.search_text) ? 1 : ((a.search_text < b.search_text) ? -1 : 0));
    },
    setStripeProductsIsLoading(state, isLoading) { state.stripeProducts.isLoading = isLoading; },
    setStripeProducts(state, data) { state.stripeProducts.data = data; },
    setSettings(state, settings) { state.settings = settings; },
    setDropdownReferralSources(state, referralSources) {
        state.dropdownReferralSources = [{ value: 0, text: "Choose one" }];
        referralSources.filter(s => s.is_selected)
            .forEach(s => state.dropdownReferralSources.push({ value: s.id, text: s.source }));
    },
    setUserRequestsAvailability(state, val) { state.userRequestsAvailability = val ? true : false; },
    clearSlotsAndAppointments(state) {
        state.slots.data = [];
        state.slots.dc = false;
        state.slots.thereAreOtherDCs = false;
        state.appointments.data = [];
    },
    setSlotsIsLoading(state, isLoading) { state.slots.isLoading = isLoading; },
    setSlots(state, slots) {
        state.slots.data = dateStringsToObjects(slots, [ "starts_at" ]);
    },
    setAppointmentsIsLoading(state, isLoading) { state.appointments.isLoading = isLoading; },
    setAppointments(state, appointments) {
        state.appointments.data = dateStringsToObjects(appointments, [ "starts_at", "ends_at" ]);
    },
    appendAppointments(state, data) {
        dateStringsToObjects(data, [ "starts_at", "ends_at" ]).forEach(appointment => { state.appointments.data.push(appointment); });
    },
    writeNpcAppointmentIntoSession(state, appointment) {
        state.session.data.has_booked_npc = true;
        state.session.data.appointment_id_npc = appointment.id;
        state.session.data.npc_json = {
            "starts_at": new Date(appointment.starts_at),
            "ends_at": new Date(appointment.ends_at)
        };
    },
    writeRofAppointmentIntoSession(state, appointment) {
        state.session.data.has_booked_rof = true;
        state.session.data.appointment_id_rof = appointment.id;
        state.session.data.rof_json = {
            "starts_at": appointment.starts_at,
            "ends_at": appointment.ends_at
        };
    },
    setSkipRofParams(state) {// After setting session data, set app params related to skipping ROF
        state.numberOfPages = 3;
        state.pageNumbers = {
            "reserve-appointment": 1,
            "about-you": 2,
            "pre-payment": 3
        };
    },
    setIsSessionExpiresSoonModalRelevant(state, value) { state.isSessionExpiresSoonModalRelevant = value; },
    setUserHasDismissedSessionExpiresSoonModal(state, value) { state.userHasDismissedSessionExpiresSoonModal = value; },
    setIsSessionExpiredModalRelevant(state, value) { state.isSessionExpiredModalRelevant = value; },
    handleGetSessionError(_state, err) {
        console.log(`mut.handleGetSessionError.err`, err);
        let message = "";
        if (err.response.data.error_type) {
            switch (err.response.data.error_type) {
                case "session-timed-out":
                    message = "It looks like your bookings session has timed out due to inactivity.";
                    break;
                case "success-page_invalid-url":
                    message = "Sorry, it looks like the web address you requested is not valid.";
                    break;
                default:
                    message = "Sorry, there's no specific message for this error.";
                    break;
            }
            this.commit("showError", { message });
        }
        else if (err.response.data.message) {
            this.commit("showError", { message });
        }
        else {
            this.commit("showError", { message: "Sorry, there's no specific message for this error." });
        }
    },
    setCheckoutData(state, checkoutData) {
        state.stripe.checkoutData = checkoutData;
    },
    showError(state, { message, data, buttonText, buttonAction }) {
        this.commit("resetError");
        state.error.message = message;
        state.error.data = data || {};
        state.error.buttonText = buttonText || "Go back...";
        state.error.buttonAction = buttonAction || false;
        state.error.show = true;
    },
    resetError(state) {
        state.error = {
            message: "",
            data: { },
            buttonText: "Back",
            buttonAction: false,
            show: false
        };
    },
    //  AVAILABILITY
    initialiseAvailability(state) {
        const dcId = (state.session.data.referred_dc_id && !state.availability.params.showAllDCs) ? state.session.data.referred_dc_id : 0;
        const startsAt = newDate();
        const endsAt = dateAdd(startsAt, 4, "weeks");

        state.availability.params.dcId = dcId;//  NPC: referred-to DC ID#, else 0
        state.availability.params.dc = state.dcs.data.find(dc => dc.id == dcId) || null;//  NPC: referred-to DC, else null
        state.availability.params.locationId = state.session.data.filters_json.location_id;
        state.availability.params.clinic = state.clinics.data.find(clinic => clinic.id == state.session.data.filters_json.location_id);
        state.availability.params.shift = "afternoon";
        state.availability.params.slots = { startsAt, endsAt };
        state.availability.params.appointments = {
            startsAt: clone(startsAt),
            endsAt: dateAdd(startsAt, 1, "week"),
            isFirstSetOfAppointments: true
        };

        state.availability.results.allSlots = {};
        state.availability.results.availableSlots = [];
        state.availability.results.currentDaysSlots = false;
        state.availability.results.slotsFilteredByShift = false;
        state.availability.results.dcs = [];
        state.availability.results.dcId = 0;
        state.availability.results.dc = false;

        state.availability.outcomes.finishedLoadingSlots = false;
        state.availability.outcomes.finishedLoadingAppointments = false;
        state.availability.outcomes.loadedFirstSetOfAppointments = false;
        state.availability.outcomes.apiReturnedSlots = false;
        state.availability.outcomes.canLoadMoreAppointments = false;
        state.availability.outcomes.isNoAvailabilityVisible = false;
        state.availability.outcomes.isNoMoreAvailabilityVisible = false;
        state.availability.outcomes.isScrollUpButtonVisible = false;
        state.availability.outcomes.isMakeAppointmentModalVisible = false;

        // console.log(`initialiseAvailability`, clone(state.availability));
    },
    setAvailabilityResultsDCs(state, dcs) {
        state.availability.results.dcs = dcs;
    },
    setAvailabilityResultDC(state, dc) {
        state.availability.results.dcId = dc.id;
        state.availability.results.dc = dc;
    },
    setAvailabilityParamsShowAllDCs(state, showAll) {
        state.availability.params.showAllDCs = showAll;
    },
    compileAvailability(state) {
        state.availability.results.dc = state.availability.params.dc;
        state.availability.results.dcId = state.availability.results.dc?.id || 0;
        state.availability.results.allSlots = clone(state.slots.data);

        state.availability.outcomes.finishedLoadingSlots = true;
        state.availability.outcomes.finishedLoadingAppointments = true;
        state.availability.outcomes.loadedFirstSetOfAppointments = true;

        //  Work out what slots are available
        const availableSlotsParams = {
            preferredDCId: state.availability.params.dcId,
            preferredDC: state.availability.params.dc,
            dcs: state.availability.results.dcs,
            clinicConsultRoomCount: state.availability.params.clinic?.consult_room_count ?? 3,
            appointmentsStartAt: state.availability.params.appointments.startsAt,
            appointmentsEndAt: state.availability.params.appointments.endsAt,
            slots: state.availability.params.dcId ? state.availability.results.allSlots.filter(slot => slot.dc_id == state.availability.params.dcId) : state.availability.results.allSlots,
            appointments: state.appointments.data,
            logMode: true
        };

        slotsCompiler.compile(availableSlotsParams);

        //  Get a representative selection of slots
        const weekOfSlots = slotsCompiler.getSelectedSlots();

        //  weekOfSlots has no inner structure, it's just a list of slots.
        //  Let's structure state.availability.results.availableSlots into a week of days, with slots in each day
        weekOfSlots.forEach(slot => {
            const dateText = dateFormat(slot.starts_at, "dddd D MMM YYYY");
            let dayOfSlots = state.availability.results.availableSlots.find(day => day.dateText == dateText);
            if (!dayOfSlots) {
                const date = clone(slot.starts_at);
                date.setHours(0, 0, 0, 0);//    slot.starts_at could be any time of day
                dayOfSlots = { date, dateText, slots: [] };
                state.availability.results.availableSlots.push(dayOfSlots);
            }
            dayOfSlots.slots.push(slot);
        });

        //  Compile outcomes
        const o = Object.assign({}, state.availability.outcomes);
        o.apiReturnedSlots = state.availability.results.allSlots.length ? true : false;
        const apptsEndsAtBeforeSlotsEndsAt = state.availability.params.appointments.endsAt.getTime() < state.availability.params.slots.endsAt.getTime();
        o.canLoadMoreAppointments = o.apiReturnedSlots && apptsEndsAtBeforeSlotsEndsAt;
        o.isNoAvailabilityVisible = !o.apiReturnedSlots;
        o.isNoMoreAvailabilityVisible = o.apiReturnedSlots && !apptsEndsAtBeforeSlotsEndsAt;
        o.loadMoreButtonIsDisabled = !o.canLoadMoreAppointments;
        o.isScrollUpButtonVisible = false;
        state.availability.outcomes = o;
    },
    initialiseLoadMoreAppointments(state, params) {
        state.availability.outcomes.finishedLoadingAppointments = false;
        state.availability.params.appointments.isFirstSetOfAppointments = false;
        state.availability.params.appointments.startsAt = params.startsAt;
        state.availability.params.appointments.endsAt = params.endsAt;
    }
};
